<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { listUser } from '@/api/system/user'

const store = useStore()

const loading = ref(false)
const showSearch = ref(true)
const query = ref({ userName: '', status: '' })
const dateRange = ref([])

const statusOptions = [
  { label: 'Enabled', value: '0' },
  { label: 'Disabled', value: '1' },
]

const columns = [
  { title: 'User ID', key: 'userId', width: 110 },
  { title: 'User Name', key: 'userName' },
  { title: 'Status', key: 'status', width: 120 },
  { title: 'Create Time', key: 'createTime', width: 180 },
]

const users = ref([])
const filteredUsers = computed(() => users.value)

const fetchUsers = async () => {
  loading.value = true
  try {
    const params = {
      pageNum: 1,
      pageSize: 200,
      userName: query.value.userName || undefined,
      status: query.value.status || undefined,
    }
    const res = await listUser(params)
    users.value = res?.rows || []
  } catch (err) {
    console.error('Failed to load users', err)
    users.value = []
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  fetchUsers()
}

const resetQuery = () => {
  query.value = { userName: '', status: '' }
  dateRange.value = []
  handleQuery()
}

const toggleStatus = (item) => {
  item.status = item.status === '0' ? '1' : '0'
}

onMounted(() => {
  fetchUsers()
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">User Management</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>Filters</v-card-title>
      <v-card-text>
        <v-form ref="queryForm" v-model="showSearch" :inline="true">
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model="query.userName" label="User Name" dense clearable hide-details />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="query.status" :items="statusOptions" item-title="label" item-value="value" label="Status" dense clearable hide-details />
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center flex-wrap gap-3">
              <v-btn size="small" color="primary" @click="handleQuery">Search</v-btn>
              <v-btn size="small" variant="tonal" color="primary" @click="resetQuery">Reset</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card class="section-card">
      <v-card-title>User List</v-card-title>
      <v-data-table
        :items="filteredUsers"
        :headers="columns"
        class="elevation-1 monitoring-table"
        :loading="loading"
        disable-sort
        disable-pagination
        hide-default-footer
        density="comfortable"
      >
        <template #item.status="{ item }">
          <v-chip :color="item.status === '0' ? 'success' : 'grey'" size="small" variant="flat" class="mr-2">
            {{ item.status === '0' ? 'Enabled' : 'Disabled' }}
          </v-chip>
          <v-switch density="compact" :model-value="item.status === '0'" @change="toggleStatus(item)" hide-details inset />
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}
</style>
